/**
 * <GovIntegrationsLandingPage /> — rebuilt per M3X.GOV.5 §4.5.
 *
 * 4 health cards (Mudad / GOSI / Qiwa / Muqeem) + amber
 * accreditation callout + bilingual error catalog card.
 *
 * Per §6 DoD: every card click-throughs to its portal page; landing
 * stays a quick-glance dashboard.
 */

import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Shield,
  Sparkles,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { HealthCard, type HealthCardData } from "../../../components/gov/HealthCard";
import { EmptyState } from "../../../components/ui/EmptyState";
import { PageHeader } from "../../../components/ui/PageHeader";
import { api } from "../../../lib/api";

const _DEMO_LE_ID = "00000000-0000-0000-0000-000000000001";

export function GovIntegrationsLandingPage() {
  const { t } = useTranslation();

  const healthQ = useQuery({
    queryKey: ["gov-health", _DEMO_LE_ID],
    queryFn: () => api.getGovHealth(_DEMO_LE_ID),
  });

  const errCatQ = useQuery({
    queryKey: ["gov-error-catalog", "en"],
    queryFn: () => api.getGovErrorCatalog("en"),
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("gov.landing_title", "Government Integrations")}
        subtitle={t(
          "gov.landing_subtitle",
          "Live status of the four KSA government portals Solvait files with on your behalf — Mudad, GOSI, Qiwa, Muqeem.",
        )}
        breadcrumbs={[
          { label: t("platform.breadcrumb_settings", "Settings"), href: "/settings" },
          {
            label: t("platform.breadcrumb_integrations", "Integrations"),
            href: "/settings/integrations",
          },
          { label: t("gov.hub_title", "Government Integrations") },
        ]}
      />

      <div
        data-testid="accreditation-callout"
        className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
      >
        <AlertTriangle size={14} aria-hidden className="mt-0.5 shrink-0" />
        <div>
          <p className="font-medium">
            {t(
              "gov.accreditation_callout_title",
              "Sandbox mode is fully demoable today",
            )}
          </p>
          <p className="mt-1">
            {t(
              "gov.accreditation_callout_body",
              "Every Mutaba'aa veto (Article 88 deadlines, M/33 amounts, IBAN match, Saudization, iqama quota) fires inline in sandbox. Production rollout per LE requires Solvait staff to confirm portal-specific accreditation.",
            )}
          </p>
        </div>
      </div>

      {healthQ.isLoading && (
        <p className="text-sm text-text-tertiary">
          {t("gov.loading", "Loading…")}
        </p>
      )}
      {healthQ.data && healthQ.data.length === 0 && (
        <EmptyState
          icon={Shield}
          headline={t("gov.empty_title", "No portal configs yet")}
          description={t(
            "gov.empty_desc",
            "Configure a Legal Entity to see portal status here.",
          )}
        />
      )}
      {healthQ.data && healthQ.data.length > 0 && (
        <div
          data-testid="health-card-grid"
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
        >
          {healthQ.data.map((card: HealthCardData) => (
            <HealthCard key={card.portal} card={card} />
          ))}
        </div>
      )}

      {/* 5th card — bilingual error catalog */}
      <section
        data-testid="error-catalog-card"
        className="rounded-xl border border-border-subtle bg-surface-default p-4"
      >
        <div className="flex items-center gap-2">
          <CheckCircle2 size={14} aria-hidden className="text-brand-blue" />
          <h3 className="text-sm font-semibold text-text-primary">
            {t("gov.error_catalog_title", "Bilingual error catalog")}
          </h3>
          <Sparkles size={11} aria-hidden className="ms-auto text-ai-deep" />
        </div>
        <p className="mt-1 text-xs text-text-tertiary">
          {t(
            "gov.error_catalog_desc",
            "Every government portal error code is mapped to plain-language EN+AR. Solvait surfaces the plain-language form; the raw code stays in the audit log.",
          )}
        </p>
        {errCatQ.data && (
          <details className="mt-3 text-xs">
            <summary className="cursor-pointer text-brand-blue hover:text-brand-deep">
              {t("gov.error_catalog_preview", "View all")} (
              {Object.keys(errCatQ.data).length})
            </summary>
            <ul className="mt-2 grid grid-cols-1 gap-1">
              {Object.values(
                errCatQ.data as Record<
                  string,
                  {
                    code: string;
                    portal: string;
                    category: string;
                    label: string;
                  }
                >,
              ).map((err) => (
                <li
                  key={err.code}
                  className="flex gap-2 rounded border border-border-subtle bg-surface-sunken p-2"
                >
                  <code className="shrink-0 text-[10px] font-mono text-text-tertiary">
                    {err.code}
                  </code>
                  <span className="text-text-secondary">{err.label}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>
    </div>
  );
}

export default GovIntegrationsLandingPage;
